import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  createContext,
  useContext,
} from 'react';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';

const ToastContext = createContext(null);

const TOAST_AUTO_DISMISS_MS = 5000;

const TOAST_VARIANT_META = {
  success: {
    variant: 'success',
    className: 'wfm-toast-success wfm-toast-popup',
    icon: CheckCircle2,
    role: 'status',
    ariaLive: 'polite',
  },
  error: {
    variant: 'error',
    className: 'wfm-toast-error wfm-toast-popup',
    icon: AlertCircle,
    role: 'alert',
    ariaLive: 'assertive',
  },
  warning: {
    variant: 'warning',
    className: 'wfm-toast-warning wfm-toast-popup',
    icon: AlertTriangle,
    role: 'alert',
    ariaLive: 'polite',
  },
  info: {
    variant: 'info',
    className: 'wfm-toast-info wfm-toast-popup',
    icon: Info,
    role: 'status',
    ariaLive: 'polite',
  },
};

/**
 * Resolves the Design System presentation metadata for a toast variant.
 * Falls back to `info` for unknown or omitted types.
 * @param {string} [type]
 * @returns {{ variant: string, className: string, icon: import('lucide-react').LucideIcon, role: string, ariaLive: string }}
 */
function resolveToastVariantMeta(type) {
  return TOAST_VARIANT_META[type] || TOAST_VARIANT_META.info;
}

let toastIdCounter = 0;

function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info') => {
    const meta = resolveToastVariantMeta(type);
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type: meta.variant }]);
    const timer = setTimeout(() => {
      timersRef.current.delete(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_AUTO_DISMISS_MS);
    timersRef.current.set(id, timer);
    return id;
  }, []);

  useEffect(() => () => {
    for (const timer of timersRef.current.values()) {
      clearTimeout(timer);
    }
    timersRef.current.clear();
  }, []);

  const toast = useMemo(() => ({
    success: (msg) => addToast(msg, 'success'),
    error: (msg) => addToast(msg, 'error'),
    info: (msg) => addToast(msg, 'info'),
    warning: (msg) => addToast(msg, 'warning'),
  }), [addToast]);

  return React.createElement(
    ToastContext.Provider,
    { value: toast },
    children,
    React.createElement(ToastContainer, { toasts, removeToast }),
  );
}

function ToastContainer({ toasts, removeToast }) {
  if (toasts.length === 0) return null;

  return React.createElement(
    'section',
    {
      className: 'wfm-toast-viewport',
      'aria-label': 'Notifiche di sistema',
    },
    toasts.map((t) => {
      const meta = resolveToastVariantMeta(t.type);
      return React.createElement(
        'div',
        {
          key: t.id,
          className: meta.className,
          role: meta.role,
          'aria-live': meta.ariaLive,
          'aria-atomic': 'true',
        },
        React.createElement(meta.icon, {
          size: 16,
          strokeWidth: 1.75,
          className: 'shrink-0',
          'aria-hidden': 'true',
        }),
        React.createElement(
          'span',
          { className: 'flex-1 min-w-0 break-words' },
          t.message,
        ),
        React.createElement(
          'button',
          {
            type: 'button',
            onClick: () => removeToast(t.id),
            className: 'wfm-toast-close',
            'aria-label': 'Chiudi notifica',
          },
          React.createElement(X, {
            size: 16,
            strokeWidth: 1.75,
            'aria-hidden': 'true',
          }),
        ),
      );
    }),
  );
}

export { ToastProvider, useToast, resolveToastVariantMeta };
